import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import IncludePropertyInEvents from "./include-feature-flag-property-in-backend-events.mdx";
import GoSetSendFeatureFlagsTrue from "./feature-flags-code-go-set-send-feature-flags-to-true.mdx";
import GoOverrideServerProperties from './override-server-properties/go.mdx';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`There are 2 steps to implement feature flags in Go:`}</p>
    <h3 {...{
      "id": "step-1-evaluate-the-feature-flag-value"
    }}>{`Step 1: Evaluate the feature flag value`}</h3>
    <h4 {...{
      "id": "boolean-feature-flags"
    }}>{`Boolean feature flags`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-go"
      }}>{`isMyFlagEnabled, err := client.IsFeatureEnabled(
            FeatureFlagPayload{
                Key:        "flag-key",
                DistinctId: "distinct_id_of_your_user",
            })

if isMyFlagEnabled == true {
    // Do something differently for this user
}
`}</code></pre>
    <h4 {...{
      "id": "multivariate-feature-flags"
    }}>{`Multivariate feature flags`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-go"
      }}>{`enabledVariant, err := client.GetFeatureFlag(
            FeatureFlagPayload{
                Key:        "flag-key",
                DistinctId: "distinct_id_of_your_user",
            })

if enabledVariant == "variant-key" { // replace 'variant-key' with the key of your variant
    // Do something differently for this user
}
`}</code></pre>

    <IncludePropertyInEvents mdxType="IncludePropertyInEvents" />
    <p>{`There are two methods you can use to include feature flag information in your events:`}</p>
    <h4 {...{
      "id": "method-1-include-the-featurefeature_flag_name-property"
    }}>{`Method 1: Include the `}<inlineCode parentName="h4">{`$feature/feature_flag_name`}</inlineCode>{` property`}</h4>
    <p>{` In the event properties, include `}<inlineCode parentName="p">{`$feature/feature_flag_name: variant_key`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-go"
      }}>{`client.Enqueue(posthog.Capture{
  DistinctId: "distinct_id_of_your_user",
  Event:      "event_name",
  Properties: posthog.NewProperties().
    Set("$feature/feature-flag-key", "variant-key"), // replace feature-flag-key with your flag key. Replace 'variant-key' with the key of your variant
})
`}</code></pre>
    <h4 {...{
      "id": "method-2-set-sendfeatureflags-to-true"
    }}>{`Method 2: Set `}<inlineCode parentName="h4">{`SendFeatureFlags`}</inlineCode>{` to `}<inlineCode parentName="h4">{`true`}</inlineCode></h4>

    <GoSetSendFeatureFlagsTrue mdxType="GoSetSendFeatureFlagsTrue" />
    <h3 {...{
      "id": "fetching-all-flags-for-a-user"
    }}>{`Fetching all flags for a user`}</h3>
    <p>{`You can fetch all flag values for a single user by calling `}<inlineCode parentName="p">{`GetAllFlags()`}</inlineCode>{`.`}</p>
    <p>{`This is useful when you need to fetch multiple flag values and don't want to make multiple requests.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-go"
      }}>{`featureVariants, _ := client.GetAllFlags(FeatureFlagPayloadNoKey{
        DistinctId: "distinct_id_of_your_user",
})
`}</code></pre>
    <h3 {...{
      "id": "sending-feature_flag_called-events"
    }}>{`Sending `}<inlineCode parentName="h3">{`$feature_flag_called`}</inlineCode>{` events`}</h3>
    <p>{`Capturing `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events enable PostHog to know when a flag was accessed by a user and thus provide `}<a parentName="p" {...{
        "href": "/docs/product-analytics/insights"
      }}>{`analytics and insights`}</a>{` on the flag. By default, we send a these event when:`}</p>
    <ol>
      <li parentName="ol">{`You call `}<inlineCode parentName="li">{`GetFeatureFlag()`}</inlineCode>{` or `}<inlineCode parentName="li">{`IsFeatureEnabled()`}</inlineCode>{`, AND `}</li>
      <li parentName="ol">{`It's a new user, or the value of the flag has changed. `}</li>
    </ol>
    <blockquote>
      <p parentName="blockquote"><em parentName="p">{`Note:`}</em>{` Tracking whether it's a new user or if a flag value has changed happens in a local cache. This means that if you reinitialize the PostHog client, the cache resets as well – causing `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events to be sent again when calling `}<inlineCode parentName="p">{`GetFeatureFlag`}</inlineCode>{` or `}<inlineCode parentName="p">{`IsFeatureEnabled`}</inlineCode>{`. PostHog is built to handle this, and so duplicate `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events won't affect your analytics.`}</p>
    </blockquote>
    <p>{`You can disable automatically capturing `}<inlineCode parentName="p">{`$feature_flag_called`}</inlineCode>{` events. For example, when you don't need the analytics, or it's being called at such a high volume that sending events slows things down.`}</p>
    <p>{`To disable it, set the `}<inlineCode parentName="p">{`SendFeatureFlagEvents`}</inlineCode>{` argument in your function call, like so:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-go"
      }}>{`isMyFlagEnabled, err := client.IsFeatureEnabled(
            FeatureFlagPayload{
                Key:                    "flag-key",
                DistinctId:             "distinct_id_of_your_user",
                SendFeatureFlagEvents:  true
            })
`}</code></pre>

    <GoOverrideServerProperties mdxType="GoOverrideServerProperties" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      